import { useEffect, type RefObject } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

export function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('data-focus-guard') && el.getAttribute('aria-hidden') !== 'true',
  );
}

/** Traps Tab / Shift+Tab inside the container while active and restores focus on release. */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean, options: { initialFocus?: boolean; restoreFocus?: boolean } = {}): void {
  const { initialFocus = true, restoreFocus = true } = options;
  useEffect(() => {
    if (!active) return;
    const root = ref.current;
    if (!root) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    if (initialFocus) {
      const autofocus = root.querySelector<HTMLElement>('[autofocus], [data-autofocus]');
      const first = autofocus ?? getFocusable(root)[0] ?? root;
      queueMicrotask(() => first.focus({ preventScroll: true }));
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable(root);
      if (focusable.length === 0) {
        e.preventDefault();
        root.focus();
        return;
      }
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      const activeEl = document.activeElement as HTMLElement | null;
      if (e.shiftKey && (activeEl === first || !root.contains(activeEl))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (restoreFocus && previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [ref, active, initialFocus, restoreFocus]);
}
